import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface User {
  id: string;
  email?: string;
  access_token?: string;
}

interface AuthContextType {
  user: User | null;
  credits: number;
  setCredits: (credits: number) => void;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  hasActiveSubscription: boolean;
  subscriptionActive: number;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(0);

  useEffect(() => {
    if (user?.id) {
      fetchCredits(user.id);
      checkAdminStatus(user.email);
      checkActiveSubscription(user.id);
      fetchSubscriptionActive(user.id);
    } else {
      setCredits(0);
      setIsAdmin(false);
      setHasActiveSubscription(false);
      setSubscriptionActive(0);
    }
  }, [user?.id, user?.email]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        console.log('[AuthContext] Initial session check:', { session, error: sessionError });
        
        if (sessionError) throw sessionError;
        
        if (session?.user) {
          setUser({
            ...session.user,
            access_token: session.access_token
          });
        }
      } catch (err) {
        console.error('[AuthContext] Error checking initial session:', err);
        setError('Failed to check authentication status');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AuthContext] Auth state changed:', event, session);
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (session?.user) {
          setUser({
            ...session.user,
            access_token: session.access_token
          });
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setCredits(0);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchCredits = async (userId: string) => {
    try {
      console.log('[AuthContext] Fetching credits for user:', userId);
      
      // First try to get credits with user role
      const { data, error } = await supabase
        .from('user_credits')
        .select('credits')
        .eq('user_id', userId)
        .single();

      console.log('[AuthContext] fetchCredits response:', { data, error });

      if (error) {
        if (error.code === 'PGRST116') {
          // Record not found, create new one using service role
          console.log('[AuthContext] No credits record found, creating new one');
          const { error: insertError } = await supabase
            .from('user_credits')
            .insert([{ 
              user_id: userId, 
              credits: 3, // Give 3 free credits to new users
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }])
            .select()
            .single();

          if (insertError) {
            console.error('[AuthContext] Error creating credits record:', insertError);
            setError('Nepodařilo se vytvořit záznam kreditů');
          } else {
            setCredits(3); // Set credits to 3 for new user
          }
        } else {
          console.error('[AuthContext] Error fetching credits:', error);
          setError('Nepodařilo se načíst kredity');
        }
        return;
      }

      if (data) {
        console.log('[AuthContext] Setting credits to:', data.credits);
        setCredits(data.credits);
      }
    } catch (err) {
      console.error('[AuthContext] Unexpected error in fetchCredits:', err);
      setError('Neočekávaná chyba při načítání kreditů');
    }
  };

  const checkAdminStatus = (email?: string) => {
    setIsAdmin(email?.toLowerCase() === 'kikin1996@seznam.cz');
  };

  const checkActiveSubscription = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('id')
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();
      setHasActiveSubscription(!!data);
    } catch (err) {
      setHasActiveSubscription(false);
    }
  };

  const fetchSubscriptionActive = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_credits')
        .select('subscription_active')
        .eq('user_id', userId)
        .single();
      setSubscriptionActive(data?.subscription_active || 0);
    } catch (err) {
      setSubscriptionActive(0);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      console.log('[AuthContext] signIn:', { data, error });
      
      if (error) throw error;
      if (!data.session) throw new Error("No session returned");
      
      setUser({
        ...data.user,
        access_token: data.session.access_token
      });
    } catch (err) {
      console.error('[AuthContext] Sign in error:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign in');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: import.meta.env.VITE_APP_URL || window.location.origin
        }
      });
      
      console.log('[AuthContext] signUp:', { data, error });
      
      if (error) throw error;
      if (!data.user) throw new Error("No user returned");
      
      // Create credits record with 3 free credits for new user
      if (data.user.id) {
        try {
          const { error: creditsError } = await supabase
            .from('user_credits')
            .insert([{ 
              user_id: data.user.id, 
              credits: 3, // Give 3 free credits to new user
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }]);

          if (creditsError) {
            console.error('[AuthContext] Error creating credits record for new user:', creditsError);
            // Don't throw error here, as user registration was successful
          } else {
            console.log('[AuthContext] Created credits record with 3 free credits for new user:', data.user.id);
          }
        } catch (creditsErr) {
          console.error('[AuthContext] Unexpected error creating credits record:', creditsErr);
          // Don't throw error here, as user registration was successful
        }
      }
      
      // Don't expect a session immediately as email confirmation is required
      if (data.session) {
        setUser({
          ...data.user,
          access_token: data.session.access_token
        });
      } else {
        // Show message about email confirmation
        setError('Please check your email to confirm your account before signing in.');
      }

      return { success: true, message: 'Please check your email to confirm your account.' };
    } catch (err) {
      console.error('[AuthContext] Sign up error:', err);
      
      // Handle rate limiting error specifically
      if (err instanceof Error && err.message.includes('security purposes')) {
        setError('Please wait a moment before trying again (rate limit reached).');
      } else if (err instanceof Error && err.message.includes('already registered')) {
        setError('This email is already registered. Please try signing in instead.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to sign up');
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      setError(null);
      const { error } = await supabase.auth.signOut();
      
      if (error) throw error;
      
      setUser(null);
      setCredits(0);
      setIsAdmin(false);
      setHasActiveSubscription(false);
      setSubscriptionActive(0);
      console.log('[AuthContext] signOut: user set to null');
    } catch (err) {
      console.error('[AuthContext] Sign out error:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign out');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setLoading(true);
      setError(null);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      
      console.log('[AuthContext] resetPassword:', { email, error });
      
      if (error) throw error;
    } catch (err) {
      console.error('[AuthContext] Reset password error:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset password');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        credits,
        setCredits,
        loading,
        error,
        isAdmin,
        hasActiveSubscription,
        subscriptionActive,
        signIn,
        signUp,
        signOut,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 